import { useState, useEffect, useCallback } from "react";
import { Plus, X, ShieldCheck } from "lucide-react";
import { getAdmins, createAdmin, toggleAdmin, changeAdminRole } from "../utils/api";
import { useAuth } from "../context/AuthContext";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const EMPTY_FORM = { name: "", email: "", password: "", role: "admin" };

export default function Admins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  // create modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [modalError, setModalError] = useState("");

  const fetchAdmins = useCallback(() => {
    getAdmins()
      .then((res) => setAdmins(res.data.admins))
      .catch((err) => setError(err.response?.data?.error || "Failed to load admins"));
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const handleToggle = async (id) => {
    setBusyId(id);
    setError("");
    try {
      await toggleAdmin(id);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally { setBusyId(null); }
  };

  const handleRoleChange = async (id, role) => {
    setBusyId(id);
    setError("");
    try {
      await changeAdminRole(id, role);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally { setBusyId(null); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setModalError("");
    if (form.password.length < 8) {
      setModalError("Password must be at least 8 characters");
      return;
    }
    setCreating(true);
    try {
      await createAdmin(form);
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchAdmins();
    } catch (err) {
      setModalError(err.response?.data?.error || "Failed to create admin");
    } finally { setCreating(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12
      }}>
        <div>
          <h1 className="page-title">Admins</h1>
          <p className="page-sub">Manage admin panel accounts. Superadmin only.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setModalError(""); }}>
          <Plus size={15} /> Create Admin
        </button>
      </div>

      {error && <div className="error-box" style={{ marginTop: 16 }}>{error}</div>}

      <div className="card table-wrap" style={{ marginTop: 18 }}>
        {!admins ? (
          <div className="empty"><span className="spinner spinner-dark" style={{ width: 26, height: 26 }} /></div>
        ) : !admins.length ? (
          <div className="empty">No admin accounts found.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Role</th>
                <th>Status</th><th>Created</th><th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const isSelf = a._id === user?.user_id;
                return (
                  <tr key={a._id}>
                    <td style={{ fontWeight: 600 }}>
                      {a.name} {isSelf && <span className="badge badge-gray" style={{ marginLeft: 6 }}>you</span>}
                    </td>
                    <td style={{ color: "#718096" }}>{a.email}</td>
                    <td>
                      <select
                        className="select"
                        style={{ width: "auto", padding: "5px 10px", fontSize: 12.5, fontWeight: 600 }}
                        value={a.role}
                        disabled={isSelf || busyId === a._id}
                        onChange={(e) => handleRoleChange(a._id, e.target.value)}
                      >
                        <option value="admin">admin</option>
                        <option value="superadmin">superadmin</option>
                      </select>
                    </td>
                    <td>
                      {a.is_active !== false
                        ? <span className="badge badge-green">Active</span>
                        : <span className="badge badge-red">Inactive</span>}
                    </td>
                    <td style={{ color: "#718096", whiteSpace: "nowrap" }}>{fmtDate(a.created_at)}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className={`btn btn-sm ${a.is_active !== false ? "btn-danger" : "btn-green"}`}
                        disabled={isSelf || busyId === a._id}
                        title={isSelf ? "You cannot deactivate yourself" : undefined}
                        onClick={() => handleToggle(a._id)}>
                        {busyId === a._id ? "…" : a.is_active !== false ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck size={18} style={{ color: "#1976e0" }} />
                <h3 style={{ fontSize: 17, fontWeight: 800 }}>Create Admin Account</h3>
              </div>
              <button onClick={() => setShowModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
                <X size={18} />
              </button>
            </div>

            {modalError && <div className="error-box" style={{ marginBottom: 14 }}>{modalError}</div>}

            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>Name</label>
                <input className="input" required value={form.name}
                  onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>Email</label>
                <input className="input" type="email" required value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="admin@thehealthhire.com" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>Password</label>
                <input className="input" type="password" required minLength={8} value={form.password}
                  onChange={(e) => set("password", e.target.value)} placeholder="Min. 8 characters" />
              </div>
              <div style={{ marginBottom: 22 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>Role</label>
                <select className="select" value={form.role} onChange={(e) => set("role", e.target.value)}>
                  <option value="admin">admin — manage users, jobs & waitlist</option>
                  <option value="superadmin">superadmin — full access incl. admins</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating && <span className="spinner" />}
                  {creating ? "Creating…" : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
